/**
 * @nanokart/tryon-studio-sdk
 * URL Catalog Provider
 *
 * Fetches product catalog from a third-party URL endpoint.
 * Supports optional custom item mapping for non-standard response formats.
 */

import {
  CatalogItem,
  CatalogFilters,
  CatalogPagination,
  CatalogResult,
} from '../../types/config';
import { CatalogProvider, ProviderCapabilities } from './types';

export interface UrlCatalogProviderOptions {
  /** The URL to fetch catalog items from (GET request) */
  catalogUrl: string;

  /**
   * Optional HTTP headers to include (e.g. Authorization)
   */
  headers?: Record<string, string>;

  /**
   * Optional mapper to convert your API response items to CatalogItem format.
   * If not provided, the response is expected to be CatalogItem[] or { items: CatalogItem[] }.
   */
  mapItem?: (raw: Record<string, unknown>) => CatalogItem;
}

/**
 * UrlCatalogProvider — fetches product catalog from a URL.
 *
 * Expected response formats (auto-detected):
 *   1. Array:  CatalogItem[]
 *   2. Object: { items: CatalogItem[] }
 *   3. Object: { products: CatalogItem[] }
 *   4. Object: { data: CatalogItem[] }
 *
 * If your format is different, provide a `mapItem` function.
 */
export class UrlCatalogProvider implements CatalogProvider {
  private cachedItems: CatalogItem[] | null = null;

  capabilities: ProviderCapabilities = {
    supportsFiltering: false,
    supportsPagination: false,
    supportsSearch: false,
  };

  constructor(private options: UrlCatalogProviderOptions) {}

  private async fetchItems(): Promise<CatalogItem[]> {
    if (this.cachedItems) return this.cachedItems;

    const response = await fetch(this.options.catalogUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        ...this.options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch catalog from ${this.options.catalogUrl}: ${response.status} ${response.statusText}`
      );
    }

    const json = await response.json();

    // Detect response format
    let rawItems: Record<string, unknown>[];
    if (Array.isArray(json)) {
      rawItems = json;
    } else if (Array.isArray(json?.items)) {
      rawItems = json.items;
    } else if (Array.isArray(json?.products)) {
      rawItems = json.products;
    } else if (Array.isArray(json?.data)) {
      rawItems = json.data;
    } else {
      throw new Error(
        `Unexpected catalog response format from ${this.options.catalogUrl}. ` +
        `Expected an array or { items: [] }. Got: ${JSON.stringify(json).slice(0, 200)}`
      );
    }

    // Map items
    const mapper = this.options.mapItem ?? defaultMapper;
    this.cachedItems = rawItems.map(mapper);
    return this.cachedItems;
  }

  async list(
    _filters?: CatalogFilters,
    _pagination?: CatalogPagination
  ): Promise<CatalogResult> {
    const items = await this.fetchItems();
    return {
      items,
      total: items.length,
      hasMore: false,
    };
  }

  async getById(productId: string): Promise<CatalogItem | null> {
    const items = await this.fetchItems();
    return items.find((item) => item.productId === productId) ?? null;
  }

  async search(query: string, _pagination?: CatalogPagination): Promise<CatalogResult> {
    const items = await this.fetchItems();
    const q = query.toLowerCase();
    const filtered = items.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.category?.toLowerCase().includes(q) ||
        item.color?.toLowerCase().includes(q)
    );
    return { items: filtered, total: filtered.length, hasMore: false };
  }

  /** Force re-fetch on next list() call */
  invalidateCache(): void {
    this.cachedItems = null;
  }
}

/**
 * Default mapper — assumes response already matches CatalogItem schema.
 * Falls back gracefully for common field name variations.
 */
function defaultMapper(raw: Record<string, unknown>): CatalogItem {
  return {
    productId: String(raw.productId ?? raw.product_id ?? raw.id ?? raw.sku ?? ''),
    name: String(raw.name ?? raw.title ?? raw.product_name ?? ''),
    imageUrl: String(raw.imageUrl ?? raw.image_url ?? raw.image ?? raw.thumbnail ?? ''),
    thumbnailUrl: raw.thumbnailUrl ? String(raw.thumbnailUrl) : undefined,
    price: Number(raw.price ?? raw.selling_price ?? 0),
    originalPrice: raw.originalPrice ? Number(raw.originalPrice) : undefined,
    currency: String(raw.currency ?? 'INR'),
    category: raw.category ? String(raw.category) : undefined,
    color: raw.color ? String(raw.color) : undefined,
    sizes: Array.isArray(raw.sizes) ? raw.sizes.map(String) : undefined,
    variantId: raw.variantId ? String(raw.variantId) : undefined,
    metadata: typeof raw.metadata === 'object' ? (raw.metadata as Record<string, unknown>) : undefined,
  };
}
